import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  QueryList,
} from '@angular/core';
import { ButtonColor } from '@freelancer/ui/button';
import { CalloutPlacement } from '@freelancer/ui/callout';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { ProgressOrientation } from '@freelancer/ui/progress-bar';
import { FontColor, FontType, FontWeight, TextSize } from '@freelancer/ui/text';
import {
  ProgressStepsBackgroundColor,
  ProgressStepsType,
} from './progress-steps.types';
import { ProgressSubstepComponent } from './progress-substep.component';

@Component({
  selector: 'fl-progress-step-callout',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProgressStepCalloutComponent {}

@Component({
  selector: 'fl-progress-step-content',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProgressStepContentComponent {}

@Component({
  selector: 'fl-progress-step-item',
  template: `
    <ng-template #stepDetails>
      <div
        class="ProgressStepIndicator"
        [class.ProgressStepIndicator-smallDot]="smallDot"
        [attr.ari-label]="indicatorLabel"
        [attr.data-current-step]="currentStep"
        [attr.data-is-done]="done && !skipped"
        [attr.data-is-skipped]="done && skipped"
        [attr.data-orientation]="orientation"
        [attr.data-background-color]="backgroundColor"
        [attr.data-mobile-large-indicator]="mobileLargeIndicator"
      >
        <ng-container
          *ngIf="stepItemType === ProgressStepsType.NUMBER; else dot"
        >
          <p
            class="text-color-inherit ItemIndicator font-bold"
            [class.ItemIndicator-notDone]="!done"
          >
            {{ stepNumber }}
          </p>
        </ng-container>

        <ng-template #dot>
          <ng-container *ngIf="smallDot === false; else isSmallDot">
            <ng-container *ngIf="done && !skipped; else notDone">
              <fl-icon
                class="ItemIndicator"
                [color]="IconColor.INHERIT"
                [name]="'ui-check'"
                [size]="
                  orientation === ProgressOrientation.HORIZONTAL
                    ? IconSize.XSMALL
                    : IconSize.SMALL
                "
                [sizeTablet]="IconSize.SMALL"
              ></fl-icon>
            </ng-container>
            <ng-template #notDone>
              <div
                class="ItemIndicatorDot"
                [class.ItemIndicatorDot-notDone]="!done"
              ></div>
            </ng-template>
          </ng-container>
          <ng-template #isSmallDot>
            <div
              class="ItemIndicatorDot ItemIndicatorDotSmall"
              [class.ItemIndicatorDot-notDone]="!done"
            ></div>
          </ng-template>
        </ng-template>
      </div>
      <div
        class="ProgressStepText"
        [attr.data-orientation]="orientation"
        [attr.data-mobile-large-indicator]="mobileLargeIndicator"
      >
        <fl-text
          class="ProgressStepTitle"
          [color]="color"
          [size]="size"
          [sizeTablet]="sizeTablet"
          [sizeDesktop]="sizeDesktop"
          [weight]="FontWeight.BOLD"
        >
          {{ stepTitle }}
        </fl-text>
      </div>
    </ng-template>
    <ng-template #content>
      <button
        class="Noborder ProgressStepIconAndTitle"
        *ngIf="clickable; else nonClickableSteps"
        [flMarginBottom]="
          orientation === ProgressOrientation.HORIZONTAL ? 'mid' : 'none'
        "
        [flMarginBottomTablet]="
          orientation === ProgressOrientation.HORIZONTAL ? 'large' : 'none'
        "
        (click)="stepClicked.emit(this.stepNumber)"
      >
        <ng-container *ngTemplateOutlet="stepDetails"></ng-container>
      </button>
      <ng-template #nonClickableSteps>
        <div
          class="ProgressStepIconAndTitle"
          [flMarginBottom]="
            orientation === ProgressOrientation.HORIZONTAL ? 'mid' : 'none'
          "
          [flMarginBottomTablet]="
            orientation === ProgressOrientation.HORIZONTAL ? 'large' : 'none'
          "
        >
          <ng-container *ngTemplateOutlet="stepDetails"></ng-container>
        </div>
      </ng-template>
      <ng-container *ngIf="showContent">
        <fl-text
          *ngIf="progressStepContent"
          class="ProgressStepDescription"
          [size]="
            orientation === ProgressOrientation.HORIZONTAL
              ? TextSize.XXSMALL
              : TextSize.XSMALL
          "
          [sizeTablet]="TextSize.XSMALL"
          [fontType]="FontType.CONTAINER"
          [flMarginBottom]="!isLastStep ? 'small' : 'none'"
        >
          <ng-content select="fl-progress-step-content"></ng-content>
        </fl-text>
      </ng-container>
      <div
        *ngIf="
          progressSubsteps?.length > 0 &&
          orientation === ProgressOrientation.VERTICAL
        "
        class="ProgressSubStepContainer"
      >
        <ng-content select="fl-progress-substep"></ng-content>
      </div>
    </ng-template>

    <ng-container *ngIf="progressStepCallout">
      <fl-callout
        [hover]="true"
        [placement]="CalloutPlacement.BOTTOM"
      >
        <fl-callout-trigger
          class="ProgressStepCalloutTrigger"
          [attr.data-orientation]="orientation"
        >
          <ng-container *ngTemplateOutlet="content"></ng-container>
        </fl-callout-trigger>
        <fl-callout-content>
          <ng-content select="fl-progress-step-callout"></ng-content>
        </fl-callout-content>
      </fl-callout>
    </ng-container>

    <ng-container *ngIf="!progressStepCallout">
      <ng-container *ngTemplateOutlet="content"></ng-container>
    </ng-container>
  `,
  styleUrls: ['./progress-step-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProgressStepItemComponent implements OnChanges, OnInit {
  ButtonColor = ButtonColor;
  CalloutPlacement = CalloutPlacement;
  IconColor = IconColor;
  IconSize = IconSize;
  FontColor = FontColor;
  TextSize = TextSize;
  FontType = FontType;
  FontWeight = FontWeight;
  ProgressOrientation = ProgressOrientation;
  ProgressStepsType = ProgressStepsType;

  isLastStep: boolean;

  @Input() backgroundColor = ProgressStepsBackgroundColor.DEFAULT;
  @Input() color = FontColor.FOREGROUND;
  @Input() completedSubstep = 0;
  @Input() currentStep = false;
  @Input() done = false;
  @Input() showContent = false;
  @Input() size: TextSize;
  @Input() sizeTablet: TextSize;
  @Input() sizeDesktop: TextSize;
  @Input() skipped = false;
  @Input() stepNumber: number;
  @Input() stepTitle: string;
  @Input() clickable = false;
  @Input() stepItemType = ProgressStepsType.NUMBER;
  @Input() smallDot = false;

  @HostBinding('attr.data-orientation')
  @Input()
  orientation: ProgressOrientation = ProgressOrientation.HORIZONTAL;

  @Input() indicatorLabel?: string;

  @Input()
  mobileLargeIndicator = false;

  /**
   * The title text at the edge will be centered if this is true.
   * Otherwise, it will be left/ right aligned with the indicator.
   */
  @HostBinding('attr.data-center-edge-text')
  @Input()
  centerEdgeText = false;

  @ContentChild(ProgressStepCalloutComponent)
  progressStepCallout: ProgressStepCalloutComponent;

  @ContentChild(ProgressStepContentComponent)
  progressStepContent: ProgressStepContentComponent;

  @ContentChildren(ProgressSubstepComponent)
  progressSubsteps: QueryList<ProgressSubstepComponent>;

  @Output() substepChanged = new EventEmitter<void>();
  @Output() stepClicked = new EventEmitter<number>();

  // FIXME: T300116 Enabling hydration errors on the webapp server
  // TypeError: Cannot read properties of null (reading 'parentElement')
  @HostBinding('attr.ngSkipHydration') skipHydration = 'true';

  constructor(
    public elementRef: ElementRef,
    public changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.sizeTablet = this.sizeTablet
      ? this.sizeTablet
      : (this.size ?? TextSize.XSMALL);
    this.sizeDesktop = this.sizeDesktop ?? this.sizeTablet;
    this.size = this.size
      ? this.size
      : this.orientation === ProgressOrientation.HORIZONTAL
        ? TextSize.XXSMALL
        : TextSize.XSMALL;
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (
      'completedSubstep' in changes &&
      this.progressSubsteps &&
      this.orientation === ProgressOrientation.VERTICAL
    ) {
      this.substepChanged.emit();
    }
  }
}
